import React, { useEffect, useState, useContext } from 'react';
import GroupAPI from 'api/GroupAPI';
import PendingInviteAPI from 'api/PendingInviteAPI';
import { GlobalContext } from 'providers/GlobalProvider';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';
import { userJoinedNotification } from '../../utils/NotificationsFuncs';
import UsersAPI from 'api/UsersAPI';
import LicensesAPI from 'api/LicensesAPI';
import { Button } from '@mui/material';
import PaymentsAPI from 'api/PaymentsAPI';
import { buildLicenseInventory } from 'utils/licenseInventory';
import { getPrimaryTeamAdmin } from 'utils/teamAdmin';

type AssignableLicenseType =
  | 'standard'
  | 'pro'
  | 'business'
  | 'enterprise'
  | 'none';

const normalizeInviteLicense = (
  value: string | undefined,
): AssignableLicenseType => {
  const normalized = String(value ?? '').toLowerCase();
  if (normalized === 'tariffs') {
    return 'standard';
  }
  if (normalized === 'starter') {
    return 'pro';
  }
  if (
    normalized === 'standard' ||
    normalized === 'pro' ||
    normalized === 'business' ||
    normalized === 'enterprise'
  ) {
    return normalized;
  }

  return 'none';
};

const JoinTeamFromInvite: React.FC = (props: any) => {
  const [teamToJoin, setTeamToJoin] = useState<any>({});
  const [displayMessage, setDisplayMessage] = useState<boolean>(false);
  const [canJoin, setCanJoin] = useState<boolean>(false);
  const [pendingInvite, setPendingInvite] = useState<any>(null);
  const { cur_user } = useContext(GlobalContext);
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  const getLicenseTotalFromCustomer = (
    customer: any,
    licenseType: Exclude<AssignableLicenseType, 'none'>,
  ) => {
    const subscriptions = Array.isArray(customer?.subscriptions?.data)
      ? customer.subscriptions.data
      : [];

    return buildLicenseInventory(subscriptions)[licenseType];
  };

  const resolveEffectiveInviteLicense = async (
    inviteLicense: AssignableLicenseType,
  ): Promise<AssignableLicenseType> => {
    if (inviteLicense === 'none') {
      return 'none';
    }

    const teamId = teamToJoin?.id;
    const teamName = teamToJoin?.name;
    const teamAdminEmail = teamToJoin?.attributes?.team_admin?.[0];
    if (!teamId || !teamName || !teamAdminEmail) {
      return 'none';
    }

    const customerRes = await PaymentsAPI.getCustomerByEmail(teamAdminEmail);
    if (!customerRes?.success) {
      return 'none';
    }

    const customers = Array.isArray(customerRes?.result?.data)
      ? customerRes.result.data
      : [];
    const customer = customers.find(
      (candidate: any) => candidate?.name === teamName,
    );
    if (!customer) {
      return 'none';
    }

    const totalForLicense = getLicenseTotalFromCustomer(
      customer,
      inviteLicense as Exclude<AssignableLicenseType, 'none'>,
    );
    if (totalForLicense <= 0) {
      return 'none';
    }

    const assignmentsRes = await LicensesAPI.getLicensesForTeam(teamId);
    if (!assignmentsRes?.success) {
      return 'none';
    }

    const assignments = Array.isArray(assignmentsRes?.result)
      ? assignmentsRes.result
      : [];
    const usedForLicense = assignments.reduce(
      (count: number, assignment: any) => {
        const assignedLicense = normalizeInviteLicense(
          assignment?.currentLicense,
        );
        if (assignedLicense === inviteLicense) {
          return count + 1;
        }
        return count;
      },
      0,
    );

    return totalForLicense - usedForLicense > 0 ? inviteLicense : 'none';
  };

  const getTeam = async () => {
    const res = await GroupAPI.getGroupById(props.match.params.id);
    if (res?.success) {
      setTeamToJoin(res.result);
      const email = cur_user?.basics?.email ?? '';
      const inviteLookup = await PendingInviteAPI.hasPendingTeamInvite(
        res.result.id,
        email,
      );
      const hasInvite = Boolean(inviteLookup?.success);
      if (hasInvite) {
        setPendingInvite(inviteLookup?.result?.data ?? null);
      } else {
        setPendingInvite(null);
        enqueueSnackbar('No pending invites found for this team.', {
          variant: 'error',
        });
        setDisplayMessage(true);
      }
      setCanJoin(hasInvite);
    }
  };

  const joinTeam = async () => {
    if (cur_user?.basics) {
      const email = cur_user?.basics?.email ?? '';
      const inviteLookup = await PendingInviteAPI.hasPendingTeamInvite(
        teamToJoin.id,
        email,
      );
      const hasInvite = Boolean(inviteLookup?.success);
      const inviteRecord = inviteLookup?.result?.data ?? pendingInvite;
      if (hasInvite) {
        const res = await GroupAPI.assignGroupToUser(
          teamToJoin.id,
          cur_user.basics.id,
        );
        if (res?.success) {
          const preassignedLicense = normalizeInviteLicense(
            inviteRecord?.preassigned_license_type,
          );
          const effectiveInviteLicense = await resolveEffectiveInviteLicense(
            preassignedLicense,
          );

          if (
            effectiveInviteLicense === 'none' &&
            preassignedLicense !== 'none'
          ) {
            enqueueSnackbar(
              `No ${preassignedLicense} license slots are currently available. Joined without a license.`,
              { variant: 'warning' },
            );
          }

          if (effectiveInviteLicense !== 'none') {
            const assignRes = await LicensesAPI.assignLicenses(teamToJoin.id, {
              changes: [
                {
                  memberId: String(cur_user.basics.id),
                  license: effectiveInviteLicense,
                },
              ],
            });

            if (!assignRes?.success) {
              enqueueSnackbar(
                `Joined team, but unable to auto-assign ${effectiveInviteLicense} license.`,
                {
                  variant: 'warning',
                },
              );
            }
          }

          await PendingInviteAPI.removePendingInvite(teamToJoin.id, email);

          // The join has already succeeded and the invite is gone, so only the
          // admin notification depends on there being a primary admin email.
          const adminEmail = getPrimaryTeamAdmin(teamToJoin);
          if (adminEmail) {
            const resp = await UsersAPI.searchUsers(adminEmail);
            const adminId = resp?.success ? resp.result?.[0]?.id : null;
            if (adminId) {
              await userJoinedNotification(cur_user, teamToJoin, adminId);
            }
          }

          enqueueSnackbar('Joined team successfully', { variant: 'success' });
          history.push('/');
          history.go(0);
        } else {
          enqueueSnackbar(
            'Error joining team. Please try again or contact us if this issue persists',
            { variant: 'error' },
          );
        }
      } else {
        enqueueSnackbar('No pending invites found for this team.', {
          variant: 'error',
        });
      }
    } else {
      enqueueSnackbar(
        'Error joining team. Please try again or contact us if this issue persists.',
        { variant: 'error' },
      );
    }
  };

  useEffect(() => {
    getTeam();
  }, []);

  return (
    <div className='join-team-container'>
      {canJoin && (
        <div className='join-team-content'>
          <h1>Team Name</h1>
          <h2>{teamToJoin?.name}</h2>
          <h3>Team Admin</h3>
          <h4>{teamToJoin?.attributes?.team_admin[0]}</h4>
          <h5>
            Once you join the team your team admin may assign you a license to
            access the features within BITE
          </h5>
        </div>
      )}
      <div className='register-btns-container'>
        {canJoin && (
          <Button onClick={() => joinTeam()} className='note-link-btn'>
            Join Team
          </Button>
        )}
        {!canJoin && displayMessage && (
          <div style={{ maxWidth: '600px' }}>
            <h3 style={{ maxWidth: '600px' }}>
              No pending invited found for this user on this team.
            </h3>
            <p>
              Please ensure that you are signed in to the correct user and
              contact the bite team at{' '}
              <span className='link'>info@bitedata.io</span> if you believe
              there is an issue.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinTeamFromInvite;
